import axios from "axios";
import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader/Loader";
import Pagination from "../../components/Pagination/Pagination";
import GradeEmployeePicker from "../../components/Grades/GradeEmployeePicker";
import { getAuthHeaders } from "../../utils/auth";

const EMPLOYEE_ASSIGN_BATCH_SIZE = 150;
const EMPLOYEE_ASSIGN_BATCH_TIMEOUT_MS = 120000;

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const hasAdminPrivileges = (role) => role === "admin" || role === "super";

const GradesManagement = ({ userProfile }) => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const statusOptions = ["All", "Active", "Inactive"];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [gradeName, setGradeName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [initialAssignedIds, setInitialAssignedIds] = useState([]);
  const [assignProgress, setAssignProgress] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = hasAdminPrivileges(userProfile?.role);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchGrades = async (page = 1) => {
    if (!canManage) return;
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const params = {
        page,
        page_size: pageSize,
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
      };
      if (statusFilter === "Active") params.active = true;
      if (statusFilter === "Inactive") params.active = false;

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/grades`,
        { headers, params }
      );
      setGrades(response.data.grades || []);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error("Error fetching grades:", error);
      alert(error.response?.data?.detail || "Failed to fetch grades.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) {
      fetchGrades(currentPage);
    } else {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, statusFilter, canManage]);

  const resetForm = () => {
    setGradeName("");
    setIsActive(true);
    setEditingGrade(null);
    setSelectedEmployeeIds([]);
    setInitialAssignedIds([]);
    setAssignProgress(null);
  };

  const loadAssignedEmployees = async (gradeId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/grades/${gradeId}/assigned-employee-ids`,
        { headers }
      );
      const ids = response.data.employee_ids || [];
      setSelectedEmployeeIds(ids);
      setInitialAssignedIds(ids);
    } catch (error) {
      console.error("Error loading assigned employees:", error);
      setSelectedEmployeeIds([]);
      setInitialAssignedIds([]);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = async (grade) => {
    setEditingGrade(grade);
    setGradeName(grade.name || "");
    setIsActive(grade.is_active !== false);
    setIsModalOpen(true);
    if (grade.id) {
      await loadAssignedEmployees(grade.id);
    }
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setIsModalOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    const trimmedName = gradeName.trim();
    if (trimmedName.length < 2) {
      alert("Grade name must be at least 2 characters.");
      return;
    }

    setSaving(true);
    setAssignProgress(null);
    try {
      const headers = await getAuthHeaders();
      const body = {
        name: trimmedName,
        is_active: isActive,
      };

      let gradeId = editingGrade?.id;
      let savedGrade = editingGrade;

      if (gradeId) {
        const response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/grades/${gradeId}`,
          body,
          { headers }
        );
        savedGrade = response.data;
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/grades`,
          body,
          { headers }
        );
        gradeId = response.data.id;
        savedGrade = response.data;
        setEditingGrade(response.data);
      }

      const baselineSet = new Set(initialAssignedIds);
      const selectedSet = new Set(selectedEmployeeIds);
      const toUnassign = initialAssignedIds.filter((id) => !selectedSet.has(id));
      const toAssign = selectedEmployeeIds.filter((id) => !baselineSet.has(id));

      const unassignBatches = chunkArray(toUnassign, EMPLOYEE_ASSIGN_BATCH_SIZE);
      const assignBatches = chunkArray(toAssign, EMPLOYEE_ASSIGN_BATCH_SIZE);
      const totalBatches = unassignBatches.length + assignBatches.length;
      let completedBatches = 0;

      try {
        if (totalBatches > 0) {
          setAssignProgress({ completed: 0, total: totalBatches });
        }

        for (const chunk of unassignBatches) {
          await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/grades/${gradeId}/employees`,
            { employee_ids: chunk, mode: "unassign" },
            { headers, timeout: EMPLOYEE_ASSIGN_BATCH_TIMEOUT_MS }
          );
          completedBatches += 1;
          setAssignProgress({ completed: completedBatches, total: totalBatches });
        }

        for (const chunk of assignBatches) {
          await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/grades/${gradeId}/employees`,
            { employee_ids: chunk, mode: "assign" },
            { headers, timeout: EMPLOYEE_ASSIGN_BATCH_TIMEOUT_MS }
          );
          completedBatches += 1;
          setAssignProgress({ completed: completedBatches, total: totalBatches });
        }

        setInitialAssignedIds([...selectedEmployeeIds]);
        await fetchGrades(currentPage);
        closeModal(true);
      } catch (assignError) {
        console.error("Error assigning employees to grade:", assignError);
        setEditingGrade(savedGrade);
        try {
          const refresh = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/grades/${gradeId}/assigned-employee-ids`,
            { headers }
          );
          const latestIds = refresh.data.employee_ids || [];
          setInitialAssignedIds(latestIds);
        } catch (refreshError) {
          console.error("Error refreshing assigned employees after failure:", refreshError);
        }
        const detail = assignError.response?.data?.detail;
        const timedOut =
          assignError.code === "ECONNABORTED" ||
          assignError.message?.toLowerCase?.().includes("timeout");
        const progressNote =
          totalBatches > 0
            ? ` (${completedBatches}/${totalBatches} batches done)`
            : "";
        alert(
          detail ||
            (timedOut
              ? `Assignment timed out${progressNote}. You can retry — remaining changes will be applied.`
              : `Failed to update employee assignments${progressNote}.`)
        );
      }
    } catch (error) {
      console.error("Error saving grade:", error);
      alert(error.response?.data?.detail || "Failed to save grade.");
    } finally {
      setSaving(false);
      setAssignProgress(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/grades/${deleteTarget.id}`,
        { headers }
      );
      setDeleteTarget(null);
      await fetchGrades(currentPage);
    } catch (error) {
      console.error("Error deleting grade:", error);
      alert(error.response?.data?.detail || "Failed to delete grade.");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  if (!canManage) {
    return (
      <div className="p-4 sm:p-5 w-full max-w-6xl mx-auto">
        <p className="text-gray-600">You do not have permission to manage grades.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 w-full min-w-0 max-w-6xl mx-auto">
      <div className="section-header !md:flex-row !flex-row !items-center bg-[#674936] text-white p-4 rounded">
        <h3 className="flex gap-4 text-white text-[1rem] md:text-[1.6rem] font-bold">
          Grade Management
        </h3>
      </div>

      <div className="my-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {statusOptions.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusChange(status)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                statusFilter === status
                  ? "bg-white text-[#674936] shadow"
                  : "bg-[#674936] text-white hover:bg-[#ebebeb] hover:shadow hover:text-[#674936]"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search grades..."
            className="border rounded px-3 py-2 text-sm w-48 sm:w-64"
          />
          <button
            type="button"
            onClick={openCreateModal}
            className="px-4 py-2 rounded bg-[#674936] text-white text-sm hover:bg-[#503220]"
          >
            Create Grade
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Employees</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center">
                  <Loader />
                </td>
              </tr>
            ) : grades.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                  No grades found.
                </td>
              </tr>
            ) : (
              grades.map((grade) => (
                <tr key={grade.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {grade.name}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {grade.employee_count ?? 0}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        grade.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {grade.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => openEditModal(grade)}
                        className="text-[#674936] underline text-xs"
                      >
                        Manage / Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(grade)}
                        className="text-red-700 underline text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="p-4 flex justify-center">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto py-4 px-2 sm:items-center">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-3xl mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editingGrade ? "Manage / Edit Grade" : "Create Grade"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Grade Name
                </label>
                <input
                  type="text"
                  value={gradeName}
                  onChange={(e) => setGradeName(e.target.value)}
                  className="mt-1 w-full border rounded px-3 py-2"
                  placeholder="e.g. Grade 1, Freelancer"
                  maxLength={100}
                  disabled={saving}
                />
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={saving}
                  className="w-4 h-4 text-[#674936] border-gray-300 rounded focus:ring-[#674936]"
                />
                Active
              </label>

              <GradeEmployeePicker
                selectedEmployeeIds={selectedEmployeeIds}
                onSelectionChange={setSelectedEmployeeIds}
                gradeId={editingGrade?.id || null}
                disabled={saving}
              />

              {assignProgress && (
                <p className="text-sm text-gray-600">
                  Updating assignments… {assignProgress.completed}/
                  {assignProgress.total} batches
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => closeModal()}
                  disabled={saving}
                  className="px-4 py-2 rounded border text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded bg-[#674936] text-white text-sm hover:bg-[#503220] disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete grade?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Delete <strong>{deleteTarget.name}</strong>? This is blocked if any
              employees are still assigned.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 rounded border text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded bg-red-700 text-white text-sm hover:bg-red-800 disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GradesManagement;
